import React from 'react';
import styles from './stylesheets/message.module.css';
import { CustomMessageText } from '../../components/CustomMessageText/CustomMessageText';
import { formatAMPM } from '../../utils/chatUtils';
import { Image } from 'primereact/image';
import { Text } from '../../components/Text';
import { ReactComponent as DownloadIcon } from '../../svg/download.svg';

const Message = ({ message, key_id }) => {
  const isInbound = message?.type === 'inbound-message';
  const isOutbound = message?.type === 'outbound-message';
  const date = formatAMPM(new Date(message.timestamp * 1000));
  const msg = message?.message || {};
  const mediaUrl = msg?.url || '';
  const caption = msg.caption || '';

  const renderMessageBody = () => {
    const { messageType } = message.message;

    switch (messageType) {
      case 'text':
        return renderText();

      case 'image':
        return renderImage();

      case 'video':
        return renderVideo();

      case 'audio':
        return renderAudio();

      case 'file':
        return renderFile();

      case 'location':
        return renderLocation();

      case 'contact':
        return renderContact();

      default:
        return renderText();
    }
  };

  const renderText = () => {
    return (
      <div className="flex w-full justify-content-between">
        <CustomMessageText
          type="sub-heading"
          style={{
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          {msg.text}
        </CustomMessageText>
      </div>
    );
  };

  const renderImage = () => {
    return (
      <div className="chatImgContainer" style={{ position: 'relative' }}>
        <Image
          src={mediaUrl}
          zoomSrc={mediaUrl}
          alt="image"
          onError={(e) => (e.target.src = '../../assets/svgIcons/svgIcons/ImgPlaceholder.svg')}
          preview
          width="100%"
          style={{ maxWidth: '300px', aspectRatio: 'auto' }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
        {caption && <CustomMessageText>{caption}</CustomMessageText>}
        <div
          className=" cursor-pointer"
          style={{
            background: 'transparent',
            top: '60px',
            left: '15px',
          }}
          onClick={() => handleDownload(mediaUrl, getDownloadFileName(message))}
        >
          <DownloadIcon />
        </div>
      </div>
    );
  };

  const renderVideo = () => {
    return (
      <div className="chatImgContainer">
        <video
          id="videoPlayer"
          controls
          width="100%"
          style={{
            objectFit: 'cover',
            width: '100%',
            maxHeight: '250px',
            aspectRatio: 'auto',
          }}
          height={'250px'}
        >
          <source src={mediaUrl} type="video/mp4" />
        </video>
        {caption && <CustomMessageText>{caption}</CustomMessageText>}
        <div
          className=" cursor-pointer"
          style={{
            background: 'transparent',
            top: '60px',
            left: '15px',
          }}
          onClick={() => handleDownload(mediaUrl, getDownloadFileName(message))}
        >
          <DownloadIcon />
        </div>
      </div>
    );
  };

  const renderAudio = () => (
    <div style={{ position: 'relative' }}>
      <audio controls>
        <source src={mediaUrl} type="audio/mpeg" />
      </audio>

      <div
        className="absolute cursor-pointer"
        style={{
          background: 'transparent',
          top: '60px',
          left: '15px',
        }}
        onClick={() => handleDownload(mediaUrl, getDownloadFileName(message))}
      >
        <DownloadIcon />
      </div>
    </div>
  );

  const renderFile = () => {
    const date = formatAMPM(new Date(message.timestamp * 1000));
    return (
      <div className="chatImgContainer">
        <div className="flex __file justify-content-center align-items-center">
          <i className="pi pi-file " style={{ fontSize: '38px', color: 'gray' }} />
          <div className="ml-1 flex flex-column  w-8 lg:w-full ">
            <span style={{ fontSize: '15px', color: 'gray' }} className="overflow-hidden	 ">
              {message.message.filename}
            </span>
            {message.rawMessage?.document.file_size && (
              <span className="mt-1" style={{ fontSize: '12px', color: 'gray' }}>
                {message.rawMessage?.document.file_size / 1000} kb
              </span>
            )}
            {caption && <CustomMessageText>{caption}</CustomMessageText>}
            <div
              className=" cursor-pointer"
              style={{
                background: 'transparent',
                top: '60px',
                left: '15px',
              }}
              onClick={() => handleDownload(mediaUrl, getDownloadFileName(message))}
            >
              <DownloadIcon />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLocation = () => (
    <div
      className="chatImgContainer"
      onClick={() =>
        window.open(
          `https://maps.google.com/?q=${message.message.latitude},${message.message.longitude}`,
          '_blank',
        )
      }
    >
      📍 View Location
    </div>
  );

  const renderContact = () => (
    <div className="chatImgContainer">
      <Text type={'heading'}>{message.message.name}</Text>
      <Text type={'sub-heading'}>{message.message.phone}</Text>
    </div>
  );

  function getDownloadFileName(message) {
    const msg = message.message || {};
    const raw = message.rawMessage || {};

    // Prefer backend filename if available
    if (msg.filename) return msg.filename;

    const baseName = msg.fileId || `media-${message.timestamp}`;

    switch (msg.messageType) {
      case 'image':
        return `${baseName}.jpg`;
      case 'video':
        return `${baseName}.mp4`;
      case 'audio':
        return `${baseName}.mp3`;
      case 'file':
        return `${baseName}.bin`;
      default:
        return `${baseName}`;
    }
  }

  async function handleDownload(url, fileName) {
    try {
      const response = await fetch(url, { mode: 'cors' });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download error:', err);

      // 🔥 fallback: open in new tab
      window.open(url, '_blank');
    }
  }

  return (
    <div
      key={key_id}
      className={`${styles.message} ${isOutbound ? styles.outboundMessage : styles.inboundMessage}`}
    >
      <div className={styles.messageContent}>
        <div className={styles.container}>
          {renderMessageBody()}

          <div className="flex justify-content-end mt-1 ml-1">
            <span className="__date">{date}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Message;
